import { FormBulletIcon, FormErrorIcon, FormInput, FormSuccessIcon } from '@dx-ui/osc-form';
import { useTranslation } from 'next-i18next';
import cx from 'classnames';
import { useFormContext, useWatch } from 'react-hook-form';
import { usernameValidation } from './form-validation-regex';
import { Text } from '@dx-ui/global-ui-prism';
import { JoinInput } from './join-form';
import { sendReward } from '@dx-ui/framework-conductrics';

const UsernameInput = () => {
  const { t } = useTranslation('osc-join-form-form');
  const usernameRequirements = [
    { type: 'mustBe', message: t('username.requirements.mustBe') },
    { type: 'englishOnly', message: t('username.requirements.englishOnly') },
    { type: 'beginWithLetter', message: t('username.requirements.beginWithLetter') },
    { type: 'noSpecialChars', message: t('username.requirements.noSpecialChars') },
  ];
  const { formState, getFieldState, control } = useFormContext<JoinInput>();
  const { error: fieldError, isTouched } = getFieldState('username', formState);
  const username = useWatch({ control, name: 'username' });
  const hasUsernameTakenError = fieldError?.type === 'exists';

  return (
    <>
      <FormInput
        registerOptions={{
          validate: {
            criteria: (value) =>
              !value ||
              Object.values(usernameValidation).every((regex) => regex.test(value)) ||
              t('username.error'),
            mustBe: (value) =>
              !value || usernameValidation.mustBe.test(value) || t('username.requirements.mustBe'),
            englishOnly: (value) =>
              !value ||
              usernameValidation.englishOnly.test(value) ||
              t('username.requirements.englishOnly'),
            beginWithLetter: (value) =>
              !value ||
              usernameValidation.beginWithLetter.test(value) ||
              t('username.requirements.beginWithLetter'),
            noSpecialChars: (value) =>
              !value ||
              usernameValidation.noSpecialChars.test(value) ||
              t('username.requirements.noSpecialChars'),
          },
        }}
        label={t('username.label')}
        name="username"
        optional={true}
        enableInlineError={false}
        labelClassName="w-full"
        aria-describedby="username-requirements"
        onFocus={() => sendReward('g-username-field-engagement')}
      />
      {hasUsernameTakenError ? (
        <div className="!mt-0 mb-2 flex items-center gap-1">
          <FormErrorIcon />
          <Text color="error" className="font-bold" size="sm">
            {t('username.error_exists')}
          </Text>
        </div>
      ) : null}
      <ul
        id="username-requirements"
        data-testid="username-requirements"
        className="text--sm !mt-2 flex flex-col gap-2"
      >
        {usernameRequirements.map(({ message, type }) => {
          const hasRequirementError = username && isTouched && fieldError?.types?.[type];
          const hasValidRequirement = username && !hasRequirementError && isTouched;
          return (
            <li
              key={`usernameRequirement-${type}`}
              className={cx('flex items-center gap-1', {
                'text-surface-error': hasRequirementError,
                'text-surface-success': hasValidRequirement,
              })}
              aria-label={message}
              data-testid={`username-requirement-${type}`}
            >
              {hasRequirementError && <FormErrorIcon />}
              {((!hasRequirementError && !isTouched) || !username) && <FormBulletIcon />}
              {hasValidRequirement && <FormSuccessIcon />}
              <Text color="inherit">{message}</Text>
            </li>
          );
        })}
      </ul>
    </>
  );
};

export default UsernameInput;
